/**
 * 移動セッション詳細シート
 *
 * MovementSessionの詳細情報を表示するボトムシート
 * - 出発地 → 到着地
 * - 所要時間、距離
 * - 移動手段の内訳
 * - 平均速度、最高速度
 */

import { ArrowRight, Clock, Gauge, MapPin, Route, Zap } from 'lucide-react'
import { useMemo } from 'react'

import { Badge } from '@/components/ui/badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import {
  formatDistanceKm,
  formatDurationMinutes,
  formatSpeed,
  formatTimeJST,
  getMotionConfig,
} from '@/lib/activity-format-utils'
import { calculateDistance } from '@/lib/geo-utils'
import type { MovementSessionWithVisits } from '@/lib/movement-session'
import { getPlaceName } from '@/lib/place-utils'

type Props = {
  session: MovementSessionWithVisits
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const MovementDetailSheet = ({ session, open, onOpenChange }: Props) => {
  const { icon: MotionIcon, color, bgColor, label } = getMotionConfig(session.motion)

  const startTime = new Date(session.startTime)
  const endTime = new Date(session.endTime)
  const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
  const distanceKm = session.estimatedDistanceKm

  // 速度情報を計算
  const speedStats = useMemo(() => {
    if (session.locations.length < 2) return { avg: 0, max: 0 }

    const speeds: number[] = []
    for (let i = 1; i < session.locations.length; i++) {
      const prev = session.locations.at(i - 1)
      const curr = session.locations.at(i)
      if (!prev || !curr) continue

      // speedフィールドがあればそれを使う (m/s → km/h)
      if (curr.speed && curr.speed > 0) {
        speeds.push(curr.speed * 3.6)
        continue
      }

      // なければ距離/時間で算出
      const dist = calculateDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude)
      const timeDiff = (new Date(curr.timestamp).getTime() - new Date(prev.timestamp).getTime()) / (1000 * 3600)
      if (timeDiff > 0) {
        const speed = dist / timeDiff
        if (speed < 200) speeds.push(speed) // 異常値を除外
      }
    }

    if (speeds.length === 0) return { avg: 0, max: 0 }
    const avg = speeds.reduce((a, b) => a + b, 0) / speeds.length
    const max = Math.max(...speeds)
    return { avg, max }
  }, [session.locations])

  // 移動手段の内訳
  const motionBreakdown = useMemo(() => {
    const counts = new Map<string, number>()
    for (const loc of session.locations) {
      for (const m of loc.motion) {
        if (m === 'stationary') continue
        counts.set(m, (counts.get(m) ?? 0) + 1)
      }
    }
    const total = Array.from(counts.values()).reduce((a, b) => a + b, 0)
    if (total === 0) return []

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([motion, count]) => ({
        motion,
        percentage: Math.round((count / total) * 100),
        ...getMotionConfig(motion),
      }))
  }, [session.locations])

  // 出発地・到着地のVisit（生成時に確定済み）
  const { departureVisit, arrivalVisit } = session

  return (
    <BottomSheet
      title={`${label} ${formatTimeJST(session.startTime)} → ${formatTimeJST(session.endTime)}`}
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={[0.5, 0.7]}
      defaultSnapPoint={0.5}
      dismissible
      noOverlay
    >
      <div className="px-4 pb-4 space-y-0">
        {/* ヘッダーアイコン */}
        <div className="flex items-center gap-2 pb-2">
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <MotionIcon className={`size-5 ${color}`} />
          </div>
          <span className="font-medium">{label}</span>
        </div>

        {/* 出発地 → 到着地 */}
        <div className="flex items-center gap-2 py-3 px-1">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="size-3.5 shrink-0" />
              <span>出発</span>
            </div>
            <p className="font-medium truncate mt-0.5">{departureVisit ? getPlaceName(departureVisit) : '---'}</p>
          </div>
          <ArrowRight className="size-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="size-3.5 shrink-0" />
              <span>到着</span>
            </div>
            <p className="font-medium truncate mt-0.5">{arrivalVisit ? getPlaceName(arrivalVisit) : '---'}</p>
          </div>
        </div>

        {/* 基本情報 */}
        <div className="grid grid-cols-2 gap-3 py-3 border-t">
          <div className="flex items-center gap-2">
            <Route className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">距離</p>
              <p className="font-medium">{formatDistanceKm(distanceKm)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">所要時間</p>
              <p className="font-medium">{formatDurationMinutes(durationMinutes)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Gauge className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">平均速度</p>
              <p className="font-medium">{formatSpeed(speedStats.avg)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">最高速度</p>
              <p className="font-medium">{formatSpeed(speedStats.max)}</p>
            </div>
          </div>
        </div>

        {/* 移動手段の内訳 */}
        {motionBreakdown.length > 1 && (
          <div className="py-3 border-t">
            <p className="text-xs text-muted-foreground mb-2">移動手段の内訳</p>
            {/* プログレスバー */}
            <div className="flex h-2 rounded-full overflow-hidden mb-2">
              {motionBreakdown.map((item) => {
                const barColor =
                  item.motion === 'driving'
                    ? '#3B82F6'
                    : item.motion === 'cycling'
                      ? '#F59E0B'
                      : item.motion === 'walking'
                        ? '#22C55E'
                        : item.motion === 'running'
                          ? '#EF4444'
                          : '#9CA3AF'
                return (
                  <div
                    key={item.motion}
                    className="h-full"
                    style={{ width: `${String(item.percentage)}%`, backgroundColor: barColor }}
                  />
                )
              })}
            </div>
            {/* ラベル */}
            <div className="flex flex-wrap gap-2">
              {motionBreakdown.map((item) => (
                <Badge key={item.motion} variant="outline" className="gap-1 text-xs">
                  <item.icon className={`size-3 ${item.color}`} />
                  {item.label} {item.percentage}%
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* ポイント数 */}
        <div className="py-3 border-t text-xs text-muted-foreground">{session.pointCount} ポイント</div>
      </div>
    </BottomSheet>
  )
}
